import React, { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { logSecurityEvent } from '@/lib/security';
import { useLanguage } from '@/contexts/LanguageContext';

interface AdminProtectedRouteProps {
  children: React.ReactNode;
}

const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({ children }) => {
  const { user, userProfile, loading, isAdmin } = useAuth();
  const { language } = useLanguage();
  const isSpanish = language === 'es';

  useEffect(() => {
    if (!loading && user) {
      logSecurityEvent('admin_route_access_attempt', {
        userId: user.id,
        isAdmin,
        route: window.location.pathname,
        timestamp: Date.now()
      }, isAdmin ? 'low' : 'high');
    }
  }, [user, loading, isAdmin]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900/20 via-background to-orange-900/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {isSpanish ? 'Verificando credenciales...' : 'Verifying credentials...'}
          </p>
        </div>
      </div>
    );
  }

  if (!user) {
    logSecurityEvent('admin_unauthorized_access_attempt', {
      route: window.location.pathname,
      timestamp: Date.now()
    }, 'high');
    return <Navigate to="/admin/login" replace />;
  }

  if (!isAdmin) {
    logSecurityEvent('admin_access_denied_non_admin', {
      userId: user.id,
      userEmail: user.email,
      route: window.location.pathname,
      timestamp: Date.now()
    }, 'high');
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900/20 via-background to-orange-900/20 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-8">
            <div className="text-red-600 text-6xl mb-4">🚫</div>
            <h1 className="text-2xl font-bold text-red-700 dark:text-red-300 mb-4">
              {isSpanish ? 'Acceso Denegado' : 'Access Denied'}
            </h1>
            <p className="text-red-600 dark:text-red-400 mb-6">
              {isSpanish 
                ? 'No tienes permisos de administrador para acceder a esta área.'
                : 'You do not have administrator permissions to access this area.'
              }
            </p>
            <div className="space-y-3">
              <button 
                onClick={() => window.location.href = '/'}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded transition-colors"
              >
                {isSpanish ? 'Ir al Inicio' : 'Go to Home'}
              </button>
              <button 
                onClick={() => window.location.href = '/admin/login'}
                className="w-full border border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/50 font-medium py-2 px-4 rounded transition-colors"
              >
                {isSpanish ? 'Acceso de Administrador' : 'Administrator Login'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AdminProtectedRoute;